import { useEffect, useRef } from "react";
import { useJoyride, EVENTS, STATUS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { tourOptions, tourStyles } from "./tourTheme";

// Wraps react-joyride v3's hook API (useJoyride) — verified against the
// package's real dist/index.d.mts, not just docs. `ready` gates the
// auto-start so it never fires against a loading skeleton or an error
// screen (see call sites in each page for what `ready` means there).
//
// `tourKey` must be one of the backend's TOUR_KEYS allow-list
// (backend/routes/auth.py) — "dashboard" | "documents" | "crosscheck" | "chat".
export function useTour(tourKey, steps, { ready = true } = {}) {
  const { user, refresh } = useAuth();
  const { t } = useTranslation("tours");
  const seen = !!user?.tours_seen?.[tourKey];
  const autoStarted = useRef(false);

  const { controls, Tour } = useJoyride({
    steps,
    continuous: true,
    options: tourOptions,
    styles: tourStyles,
    locale: {
      back: t("buttons.back"),
      close: t("buttons.next"),
      last: t("buttons.finish"),
      next: t("buttons.next"),
      skip: t("buttons.skip"),
    },
    onEvent: (data) => {
      if (
        data.type === EVENTS.TOUR_END &&
        !seen &&
        (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED)
      ) {
        api.patch(`/auth/me/tours/${tourKey}`).then(() => refresh()).catch(() => {});
      }
    },
  });

  useEffect(() => {
    if (ready && user && !seen && !autoStarted.current) {
      autoStarted.current = true;
      controls.start();
    }
  }, [ready, user, seen, controls]);

  return { Tour, replay: () => controls.start() };
}
